import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import useSWR from "swr";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "../../../svgs/search.svg";
import UploadIcon from "../../../svgs/upload-file.svg";
import AddCircleIcon from "../../../svgs/add-circle.svg";
import ContentTabs from "../../POSupplierDevice/components/ContentTabs";
import WarehouseStockTable from "./WarehouseStockTable";
import WarehouseTransferTable from "./WarehouseTransferTable";
import InventoryTransferModal from "./InventoryTransferModal";
import UploadModal from "../../Common/UploadModal";
import PaginationBar from "../../Common/PaginationBar";
import {
  inventoryFetcher,
  InventoryService,
} from "../../../network/InventoryService";
import { useNotification } from "../../../../Notifications/NotificationProvider";
import useDebounce from "../../../../Shared/GeographyFilters/hooks/useDebounce";
import { DEFAULT_PAGE_SIZE } from "../../../utils/inventoryHelper";

const WarehouseDetails = ({ warehouse, onBack }) => {
  const location = useLocation();
  const history = useHistory();

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    const detailTabFromUrl = params.get("detailTab");
    const defaultDetailTab = "stock";

    if (detailTabFromUrl && ["stock", "transfers"].includes(detailTabFromUrl)) {
      return detailTabFromUrl;
    }
    return defaultDetailTab;
  });
  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [transferPage, setTransferPage] = useState(1);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [inventoryTypeForm, setInventoryTypeForm] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const dispatchNotification = useNotification();
  const userType = JSON.parse(localStorage.getItem("currUserType"));
  const debouncedSearchTerm = useDebounce(localSearchTerm, 400);

  useEffect(() => {
    setSearchTerm(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  const detailTabs = [
    { id: "stock", label: "Stock" },
    { id: "transfers", label: "Transfers" },
  ];
  const stockApiUrl = useMemo(() => {
    const params = new URLSearchParams({
      id: warehouse?.id || 1,
      type: "warehouse",
    });

    if (searchTerm) {
      params.append("search_term", searchTerm);
    }

    return `${
      process.env.REACT_APP_API_URL
    }/api/payment_speaker/v1/device-count?${params.toString()}`;
  }, [searchTerm, warehouse.id]);

  const {
    data: stockResponse,
    error: stockError,
    mutate: mutateStock,
  } = useSWR(stockApiUrl, inventoryFetcher, {
    fallbackData: [],
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    errorRetryCount: 3,
    errorRetryInterval: 1000,
    refreshInterval: 5 * 60 * 1000,
  });

  const stockData = stockResponse || [];
  const stockLoading = !stockResponse && !stockError;
  const transferApiUrl = useMemo(() => {
    const params = new URLSearchParams({
      id: warehouse?.id || 1,
      type: "warehouse",
      page_index: transferPage - 1,
      page_size: DEFAULT_PAGE_SIZE,
    });

    if (searchTerm && activeTab === "transfers") {
      params.append("search_term", searchTerm);
    }

    return `${
      process.env.REACT_APP_API_URL
    }/api/payment_speaker/v1/psb-transfer?${params.toString()}`;
  }, [warehouse.id, searchTerm, activeTab, transferPage]);

  const { data: transferResponse, error: transferError } = useSWR(
    activeTab === "transfers" ? transferApiUrl : null,
    inventoryFetcher,
    {
      fallbackData: [],
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      errorRetryCount: 3,
      errorRetryInterval: 1000,
      refreshInterval: 5 * 60 * 1000,
    }
  );

  const transferData = transferResponse?.data || [];
  const transferTotalCount = transferResponse?.count || 0;
  const transferTotalPages = Math.ceil(transferTotalCount / DEFAULT_PAGE_SIZE);
  const transferLoading =
    activeTab === "transfers" && !transferResponse && !transferError;

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
      ];

      if (allowedTypes.includes(file.type) || file.name.endsWith(".csv")) {
        setUploadedFile(file);
      } else {
        dispatchNotification({
          type: "ERROR",
          message: "Please upload only Excel (.xlsx, .xls) or CSV files.",
        });
      }
    }
    event.target.value = "";
  };

  const handleDownloadSample = async () => {
    try {
      const endpoint = `${process.env.REACT_APP_API_URL}/api/payment_speaker/upload/receive-stock?inventory_type=${inventoryTypeForm}`;
      await InventoryService.downloadSampleFile(
        endpoint,
        "stock-receive-sample.xlsx"
      );
    } catch (error) {
      console.error("Error downloading sample file:", error);
      dispatchNotification({
        type: "ERROR",
        message: "Failed to download sample file. Please try again.",
      });
    }
  };

  const handleUploadSubmit = async () => {
    if (!inventoryTypeForm) {
      dispatchNotification({
        type: "ERROR",
        message: "Please select an inventory type.",
      });
      return;
    }

    if (!uploadedFile) {
      dispatchNotification({
        type: "ERROR",
        message: "Please upload a file.",
      });
      return;
    }

    setIsUploading(true);

    try {
      const response = await InventoryService.uploadReceiveStock(
        inventoryTypeForm,
        uploadedFile
      );

      if (response.is_error === 0) {
        dispatchNotification({
          type: "SUCCESS",
          message: "Stock receive status updated successfully!",
        });

        setInventoryTypeForm("");
        setUploadedFile(null);
        setShowUploadModal(false);
        mutateStock();
      } else {
        throw new Error(
          response.message || "Failed to update stock receive status"
        );
      }
    } catch (error) {
      console.error("Error uploading receive stock:", error);

      let errorMessage =
        "Failed to update stock receive status. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      dispatchNotification({
        type: "ERROR",
        message: errorMessage,
      });
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const currentDetailTab = params.get("detailTab");

    if (
      !currentDetailTab ||
      !["stock", "transfers"].includes(currentDetailTab)
    ) {
      params.set("detailTab", activeTab);
      history.replace(`${location.pathname}?${params.toString()}`);
    } else if (activeTab !== currentDetailTab) {
      params.set("detailTab", activeTab);
      history.replace(`${location.pathname}?${params.toString()}`);
    }
  }, [activeTab, history, location.search, location.pathname]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const renderStockContent = () => (
    <>
      <div className="content-header">
        <ContentTabs
          tabs={detailTabs}
          contentTab={activeTab}
          setContentTab={handleTabChange}
        />
        <div className="filters-section">
          <div className="search-input">
            <img src={SearchIcon} alt="Search Icon" className="search-icon" />
            <input
              type="text"
              value={localSearchTerm}
              onChange={(e) => setLocalSearchTerm(e.target.value)}
              placeholder="Search"
              className="search-input-field"
            />
          </div>
          <div className="stock-actions">
            {userType !== "bkash-super-user" && (
              <button
                className="upload-btn"
                onClick={() => setShowUploadModal(true)}
              >
                <img src={UploadIcon} alt="Upload Icon" className="btn-icon" />
                Bulk Upload Stock Receive Status
              </button>
            )}
            {/* <button className="download-btn">
              <img
                src={DownloadIcon}
                alt="Download Icon"
                className="btn-icon"
              />
              Download Stock Report
            </button> */}
          </div>
        </div>
      </div>
      <div className="inventory-table-container">
        <WarehouseStockTable
          stockData={stockData}
          loading={stockLoading}
          error={stockError}
          onRetry={mutateStock}
          searchTerm={searchTerm}
        />
      </div>
    </>
  );

  useEffect(() => {
    setTransferPage(1);
  }, [searchTerm, activeTab]);

  const renderTransfersContent = () => (
    <>
      <div className="content-header">
        <ContentTabs
          tabs={detailTabs}
          contentTab={activeTab}
          setContentTab={handleTabChange}
        />
        <div className="filters-section">
          <div className="search-input">
            <img src={SearchIcon} alt="Search Icon" className="search-icon" />
            <input
              type="text"
              value={localSearchTerm}
              onChange={(e) => setLocalSearchTerm(e.target.value)}
              placeholder="Search"
              className="search-input-field"
            />
          </div>
          {userType !== "bkash-super-user" && (
            <button
              className="create-btn"
              onClick={() => setShowTransferModal(true)}
            >
              <img src={AddCircleIcon} alt="Add Icon" className="btn-icon" />
              Initiate A New Transfer
            </button>
          )}
        </div>
      </div>
      <div className="inventory-table-container">
        <WarehouseTransferTable
          page={transferPage}
          transferData={transferData}
          loading={transferLoading}
          error={transferError}
          searchTerm={searchTerm}
        />
      </div>
      {transferTotalCount > 0 && !transferLoading && !transferError && (
        <PaginationBar
          page={transferPage}
          setPage={setTransferPage}
          totalPages={transferTotalPages}
          totalCount={transferTotalCount}
        />
      )}
    </>
  );

  const handleBackClick = () => {
    const params = new URLSearchParams(location.search);
    params.delete("detailTab");
    history.replace(`${location.pathname}?${params.toString()}`);
    onBack();
  };

  return (
    <div className="content-section">
      <div className="content-header">
        <div className="warehouse-details-header">
          <button className="back-btn" onClick={handleBackClick}>
            <ArrowBackIcon style={{ fontSize: 20 }} />
          </button>
          <h2 className="warehouse-title">{warehouse.name}</h2>
        </div>
      </div>
      <div className="inventory-content-container">
        {activeTab === "stock"
          ? renderStockContent()
          : renderTransfersContent()}
      </div>
      <UploadModal
        showUploadModal={showUploadModal}
        setShowUploadModal={setShowUploadModal}
        inventoryTypeForm={inventoryTypeForm}
        setInventoryTypeForm={setInventoryTypeForm}
        uploadedFile={uploadedFile}
        setUploadedFile={setUploadedFile}
        handleFileUpload={handleFileUpload}
        handleDownloadSample={handleDownloadSample}
        handleUploadSubmit={handleUploadSubmit}
        isUploading={isUploading}
        modalClassName="inventory-modal inventory-stock-upload-modal"
      />

      {showTransferModal && (
        <InventoryTransferModal
          showTransferModal={showTransferModal}
          setShowTransferModal={setShowTransferModal}
          type="warehouse"
          sourceData={warehouse}
        />
      )}
    </div>
  );
};

export default WarehouseDetails;
